import { ArrowUp, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { streamSmartDocsQuery } from '../services/chatService';
import { getUserIdFromToken } from '../utils/authUtils';
import { useModuleContext } from '../context/ModuleContext';
import { streamFocus360Query } from '../services/focus360Service';
import { streamEnergyOptimizationInsights } from '../services/energyOptimizationService';
import { v4 as uuidv4 } from 'uuid';

const CommonTextBoxArea = ({
  id,
  category = 'Calendar',
  subcategory = 'Troubleshooting',
  showCategoryAndSubcategory = false,
  onMessageAdd,
  onStreamUpdateEnergyOptimization,
  onHeaderCreated,
  onStreamComplete,
}) => {
  const [inputValue, setInputValue] = useState('');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { selectedModule, getCurrentApplicationId } = useModuleContext();



  const handleSubmit = async (e) => {
    e.preventDefault();
    const userId = getUserIdFromToken();
    const applicationId = getCurrentApplicationId();

    if (!inputValue.trim()) return;
    if (!userId || !applicationId) return;

    const isNewChat = !id;
    const chatId = id || uuidv4();
    const question = inputValue.trim();

    setIsLoading(true);

    try {

      if (selectedModule === 'energy-optimization') {
        const payload = {
          chat_id: chatId,
          application_id: applicationId,
          is_new_session: isNewChat,
          user_id: userId,
          query: question,
        };

        // Add messages first before navigation
        onMessageAdd?.({ role: 'user', content: inputValue });
        setInputValue('');
        onMessageAdd?.({ role: 'assistant', content: '', isLoading: true, feedback: null });

        if (isNewChat) {
          navigate(`/chat/${chatId}`);
        }

        await streamEnergyOptimizationInsights(payload, {
          onChunk: (partialContent) => {
            onStreamUpdateEnergyOptimization?.(partialContent);
          },
          onHeaderCreated: (header, conversationId) => {
            onHeaderCreated?.(header, conversationId);
          },
          onStreamComplete: (conversationId, pairId, images) => {
            onStreamComplete?.(conversationId, pairId, images);
          },
        });

        setIsLoading(false);
        return;
      }

      if (selectedModule === 'focus360') {
        const payload = {
          chat_id: chatId,
          query: question,
          customer_id: 'CIP001',
          location: 'IN',
          site_id: ['CIP001IN15'],
          is_new_session: isNewChat,
          application_id: applicationId,
          user_id: userId,
        };

        // Add messages first before navigation
        onMessageAdd?.({ role: 'user', content: inputValue });
        setInputValue('');
        onMessageAdd?.({ role: 'assistant', content: '', isLoading: true, feedback: null });

        if (isNewChat) {
          navigate(`/chat/${chatId}`);
        }

        await streamFocus360Query(payload, {
          onChunk: (partialContent) => {
            onStreamUpdateEnergyOptimization?.(partialContent);
          },
          onHeaderCreated: (header, conversationId) => {
            onHeaderCreated?.(header, conversationId);
          },
          onStreamComplete: (conversationId, pairId) => {
            onStreamComplete?.(conversationId, pairId);
          },
        });

        setIsLoading(false);
        return;
      }



      // Add messages first before navigation
      onMessageAdd?.({ role: 'user', content: inputValue });
      setInputValue('');
      onMessageAdd?.({ role: 'assistant', content: '', isLoading: true, feedback: null });

      if (isNewChat) {
        navigate(`/chat/${chatId}`);
      }

      await streamSmartDocsQuery({
        application_id: applicationId,
        user_id: userId,
        question: question,
        category,
        sub_category: subcategory,
        chat_id: chatId,
        is_new_session: isNewChat,
      }, {
        onChunk: (partialContent) => {
          onStreamUpdateEnergyOptimization?.(partialContent);
        },
        onHeaderCreated: (header, conversationId) => {
          onHeaderCreated?.(header, conversationId);
        },
        onStreamComplete: (conversationId, pairId) => {
          onStreamComplete?.(conversationId, pairId);
        },
      });

      setIsLoading(false);
    } catch (error) {
      console.error('Chat API error:', error);
    } finally {
      setIsLoading(false); // stop loading
    }
  };

  return (
    <form
      className="flex flex-col p-3 w-full max-w-4xl mx-auto bg-white/20 border border-white/40 rounded-xl shadow-sm"
      onSubmit={handleSubmit}
    >
      <textarea
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (inputValue.trim()) {
              e.currentTarget.form?.requestSubmit();
            }
          }
        }}
        className="w-full bg-transparent resize-none rounded p-1 grow outline-none placeholder:text-white/50"
        name="message-input"
        id="message"
        rows={2}
        placeholder="Ask anything"
      ></textarea>

      <div className="flex sm:flex-row w-full mt-2 items-stretch sm:items-center gap-2" id="action-area">
        {/* Show category and subcategory text for smart-assistant module on chat details page */}
        {showCategoryAndSubcategory && id && category && subcategory && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 bg-white/5 rounded-lg">
            <span className="text-gray-400">Category:</span>
            <span className="text-white font-medium">{category}</span>
            <span className="text-gray-400">|</span>
            <span className="text-gray-400">Subcategory:</span>
            <span className="text-white font-medium">{subcategory}</span>
          </div>
        )}

        <button
          className="ml-auto px-4 py-2 text-white bg-black rounded-md hover:bg-black/75 sm:ml-auto transition-all disabled:bg-gray-500 disabled:cursor-not-allowed min-w-[44px] flex items-center justify-center"
          type="submit"
          disabled={!inputValue.trim() || isLoading}
        >
          {isLoading ? (
              <LoaderCircle className="animate-spin" />
          ) : (
            <ArrowUp />
          )}
        </button>
      </div>
    </form>
  );
};

export default CommonTextBoxArea;